import {DatePicker, Form, Input} from 'antd';
import SubmitButton from 'components/Startup/components/SubmitButton';
import moment from 'moment';
import {Fragment, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';
import plusIcon from 'images/plus-circle.png';

import {listingIPO} from 'redux/store/campaign';
import IpoFeeCard from './ipoFeeCard/IpoFeeCard';
import styles from './listingIPO.module.scss';
import StageHeader from 'pages/campaigns/new/components/StageHeader';
import ErrorSummary from 'pages/campaigns/new/components/ErrorSummary';
import FieldError from 'pages/campaigns/new/components/FieldError';
import {useStageValidation} from 'pages/campaigns/new/components/useStageValidation';

const ListingIPO = () => {
  const [numSharesAllocated, setNumSharesAllocated] = useState(0);
  const [expectedIpo, setExpectedIpo] = useState('');
  const [offeringName, setOfferingName] = useState('');
  const [ipoSharePrice, setIpoSharePrice] = useState(0);
  const [numOfOutstandingShares, setNumOfOutstandingShares] = useState(0);
  const {slug, id, companyId} = useParams();
  const [ipoFeesValues, setIpoFeesValues] = useState([
    {
      feeName: '',
      feeType: '',
      fixedFeeAmount: '',
      variableFeeAmount: '',
      feeAppliesTo: '',
    },
  ]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const campaign = useSelector(state => state.campaign.campaign);
  const {errors, validate, clearError} = useStageValidation();

  useEffect(() => {
    if (campaign?.numSharesAllocated) {
      setNumSharesAllocated(campaign?.numSharesAllocated);
      setExpectedIpo(campaign?.expectedIpo);
      setOfferingName(campaign?.offeringName);
      setIpoSharePrice(campaign?.ipoSharePrice);
      setNumOfOutstandingShares(campaign?.numOfOutstandingShares);
      setOfferingName(campaign?.offeringName);
    }
  }, [campaign?.numSharesAllocated]);

  useEffect(() => {
    if (campaign?.ipoFeesValues) {
      setIpoFeesValues(
        campaign?.ipoFeesValues?.map(data => ({
          feeName: data?.feeName,
          feeType: data?.feeType,
          fixedFeeAmount: data?.fixedFeeAmount,
          variableFeeAmount: data?.variableFeeAmount,
          feeAppliesTo: data?.feeAppliesTo || '',
        })),
      );
    }
  }, [campaign?.ipoFeesValues]);

  const handleChangeIpoFeesValues = (index, event) => {
    const newFormValues = [...ipoFeesValues];
    newFormValues[index][event.target.name] = event.target.value;
    setIpoFeesValues(newFormValues);
  };

  const handleChangeIpoFeesType = (index, event) => {
    const newFormValues = [...ipoFeesValues];
    newFormValues[index].feeType = event;
    if (event !== 'ENTRY') {
      newFormValues[index].feeAppliesTo = '';
    }
    setIpoFeesValues(newFormValues);
  };

  const handleChangeIpoFeesAppliesTo = (index, value) => {
    const newFormValues = [...ipoFeesValues];
    newFormValues[index].feeAppliesTo = value;
    setIpoFeesValues(newFormValues);
  };

  const handleAddIpoFees = () => {
    setIpoFeesValues([
      ...ipoFeesValues,
      {
        feeName: '',
        feeType: '',
        fixedFeeAmount: '',
        variableFeeAmount: '',
        feeAppliesTo: '',
      },
    ]);
  };

  const handleDeleteIpoFees = id => {
    const filteredIpoFee = ipoFeesValues.filter((_, index) => index !== id);
    setIpoFeesValues(filteredIpoFee);
  };

  const handleSubmit = () => {
    const rules = [
      {
        field: 'numSharesAllocated',
        valid: !!numSharesAllocated,
        message: 'Number of Shares Allocated is required',
      },
      {
        field: 'offeringName',
        valid: !!offeringName,
        message: 'Offering Name is required',
      },
      {
        field: 'ipoSharePrice',
        valid: !!ipoSharePrice,
        message: 'IPO Share Price is required',
      },
      {
        field: 'numOfOutstandingShares',
        valid: !!numOfOutstandingShares,
        message: 'Total Number of Shares outstanding for IPO is required',
      },
      {
        field: 'expectedIpo',
        valid: !!expectedIpo,
        message: 'Expected IPO Date is required',
      },
    ];
    if (!validate(rules)) return;
    dispatch(
      listingIPO({
        numSharesAllocated,
        expectedIpo,
        ipoSharePrice,
        ipoFeesValues,
        numOfOutstandingShares,
        offeringName,
      }),
    );
    if (slug) {
      navigate(`/dashboard/campaigns/${slug}/new/${id}/founders/${companyId}`);
    } else {
      navigate(`/dashboard/campaigns/new/${id}/documents/${companyId}`);
    }
  };

  return (
    <div className={styles.container}>
      <StageHeader
        title="IPO Details"
        description="IPO-specific details like shares, offering name, and expected date."
      />
      <Form layout="vertical">
        <ErrorSummary fields={Object.values(errors)} />
        <Form.Item
          className={styles['form-item']}
          label="Number of Shares Allocated"
          name="numSharesAllocated"
          rules={[
            {
              required: true,
              message: 'Required Field',
            },
          ]}
          initialValue={campaign?.numSharesAllocated}>
          <Input
            defaultValue={campaign?.numSharesAllocated}
            value={numSharesAllocated}
            type="number"
            onChange={e => {
              setNumSharesAllocated(e.target.value);
              clearError('numSharesAllocated');
            }}
          />
        </Form.Item>
        <FieldError message={errors.numSharesAllocated} />
        <Form.Item
          className={styles['form-item']}
          label="Offering Name"
          name="offeringName"
          rules={[
            {
              required: true,
              message: 'Required Field',
            },
          ]}
          initialValue={campaign?.offeringName}>
          <Input
            defaultValue={campaign?.offeringName}
            value={offeringName}
            onChange={e => {
              setOfferingName(e.target.value);
              clearError('offeringName');
            }}
          />
        </Form.Item>
        <FieldError message={errors.offeringName} />
        <Form.Item
          className={styles['form-item']}
          label="IPO Share Price"
          rules={[
            {
              required: true,
              message: 'Required Field',
            },
          ]}
          name="ipoSharePrice"
          initialValue={campaign?.ipoSharePrice}>
          <Input
            defaultValue={campaign?.ipoSharePrice}
            value={ipoSharePrice}
            onChange={e => {
              setIpoSharePrice(e.target.value);
              clearError('ipoSharePrice');
            }}
            type="number"
          />
        </Form.Item>
        <FieldError message={errors.ipoSharePrice} />
        <Form.Item
          label="Total Number of Shares outstanding for IPO"
          name="numOfOutstandingShares"
          rules={[
            {
              required: true,
              message: 'Required Field',
            },
          ]}
          className={styles['form-item']}
          initialValue={campaign?.numOfOutstandingShares}>
          <Input
            value={numOfOutstandingShares}
            onChange={e => {
              setNumOfOutstandingShares(e.target.value);
              clearError('numOfOutstandingShares');
            }}
            type="number"
            defaultValue={campaign?.numOfOutstandingShares}
          />
        </Form.Item>
        <FieldError message={errors.numOfOutstandingShares} />
        <Form.Item
          label="Expected IPO Date"
          name="date"
          className={styles['form-item']}
          rules={[
            {
              required: true,
              message: 'Required Field',
            },
          ]}
          initialValue={
            campaign.expectedIpo
              ? moment(campaign?.expectedIpo, 'YYYY-MM-DD HH:mm:ss')
              : ''
          }>
          <DatePicker
            placeholder="Select Date Time"
            value={expectedIpo}
            defaultValue={
              campaign.expectedIpo
                ? moment(campaign?.expectedIpo, 'YYYY-MM-DD HH:mm:ss')
                : ''
            }
            showTime={{
              defaultValue: moment('00:00', 'HH:mm:ss'),
            }}
            format={'YYYY-MM-DD HH:mm:ss'}
            onChange={e => {
              setExpectedIpo(moment(e).format('YYYY-MM-DD HH:mm:ss'));
              clearError('expectedIpo');
            }}
            style={{width: 340}}
          />
        </Form.Item>
        <FieldError message={errors.expectedIpo} />
        <div>
          <h1>IPO Investment Details</h1>
        </div>
        <div className={styles.form}>
          {ipoFeesValues.map((data, index) => (
            <Fragment key={index}>
              <IpoFeeCard
                feeName={data.feeName || ''}
                feeType={data.feeType}
                fixedFeeAmount={data.fixedFeeAmount}
                variableFeeAmount={data.variableFeeAmount}
                feeAppliesTo={data.feeAppliesTo}
                handleChangeIpoFeesValues={e =>
                  handleChangeIpoFeesValues(index, e)
                }
                handleChangeIpoFeesType={e => handleChangeIpoFeesType(index, e)}
                handleChangeIpoFeesAppliesTo={value =>
                  handleChangeIpoFeesAppliesTo(index, value)
                }
                handleDeleteIpoFees={e => handleDeleteIpoFees(index, e)}
              />
            </Fragment>
          ))}
        </div>
        <div onClick={handleAddIpoFees} className={styles['add-button']}>
          <img src={plusIcon} alt="" />
          <h2>Add Field</h2>
        </div>

        <SubmitButton
          type="button"
          label="Save Changes"
          handleClick={handleSubmit}
        />
      </Form>
    </div>
  );
};

export default ListingIPO;
